import { useCallback, useEffect, useRef } from 'react';
import logger from 'redux-logger';
import { nanoid } from 'nanoid';

import { useMutRef } from '@toolbox/hooks';
import { noop } from '@toolbox/utils';
import { reducer, initState } from './reducer';
import { useEventMiddleware } from './utils/useEventMiddleware';
import { useEnhancedReducer } from './utils/useEnhancedReducer';
import { thunkMiddleware } from './utils/thunkMiddleware';
import { accumulateFormData, calculateNextStep } from './reducer/calculateNextState';
import { pushGAEvent } from '../analytics';
import { getAnalyticsFields } from '@widget/utils/getAnalyticsFields';
import { getSkippedFields } from './utils/getSkippedFields';

import type { AppointmentType } from '@widget/api/types';
import type { AnalyticsMap } from '@widget/utils/getAnalyticsFields';
import type { LeadData } from '@widget/core/LeadDataProvider';
import type { StepProps } from './useStepForm';
import type {
  InitialWidgetState,
  ResetHistoryAction,
  CommitAction,
  ReducerActions,
  SkipAction,
  GoToStepAction,
} from './reducer';
import type { ImageAsset, StepState, WidgetState, WidgetSubmitTarget } from './types';
import type { WidgetEvent } from './EventsProvider';
import type { Middleware } from './utils/useEnhancedReducer';
import type { ThunkDispatch } from './utils/thunkMiddleware';

export interface UseWidgetProps {
  enableReset?: boolean;
  cancelIcon?: ImageAsset;

  /**
   * Initial widget state
   */
  initialState?: InitialWidgetState;
  /**
   * event handler for all the widget events
   */
  on?: (event: WidgetEvent) => void;
  /**
   * step configurations
   */
  steps: StepProps[];
  /**
   * callback to be called when history is changed
   */
  onChange?: (formData: Record<string, unknown>) => void | Promise<void>;
  onCommit: (formData: Record<string, unknown>) => void | Promise<void>;
  /**
   * callback to be called when submitting the widget data
   */
  onSubmitLead: (formData: Record<string, unknown>) => void | Promise<void>;
  onUpdateLead: (formData: Record<string, unknown>) => void | Promise<void>;
  onRequestTimeSlots: (args: {
    leadId?: string;
    zipCode?: string;
    appointmentType: AppointmentType;
    timezone: string;
  }) => void | Promise<void>;
  /**
   * callback to be called when closing the widget
   */
  onClose?: () => void | Promise<void>;

  widgetId: string;
  leadData: LeadData;
  analyticsMap?: AnalyticsMap;
  // expectRenderWhen?: (query: Record<string, unknown>, formData: Record<string, unknown>) => boolean;
}

export type CommitFn = (
  id: string,
  data: Record<string, unknown>,
  nextStepId?: string,
) => Promise<void> | void;

export type WidgetStateMachine = {
  getFormData: () => Record<string, unknown>;
  commit: CommitFn;
  submit: (target: WidgetSubmitTarget) => Promise<void>;
  onRequestTimeSlots: (args: {
    leadId?: string;
    zipCode?: string;
    appointmentType: AppointmentType;
    timezone: string;
  }) => void | Promise<void>;
  close: () => Promise<void>;
  reset: () => void;
  skip: (id: string, nextStepId?: string) => void;
  getStep: <FD extends Record<string, unknown> = Record<string, unknown>>(
    id: string,
  ) => StepState<FD> | undefined;
  getStepProps: (id: string) => StepProps | undefined;
  history: StepState[];
  activeId: string;
  stepIndex: number;
  back: () => void;
  next: () => void;
  enableReset?: boolean;
  cancelIcon?: ImageAsset;
  goTo: (id: string) => void;
  leadData: LeadData;
  prevStepId?: string;
  transactionId: string;
  widgetId: string;
  expectRenderWhen?: (query: Record<string, unknown>, formData: Record<string, unknown>) => boolean;
};

declare module './EventsProvider' {
  export interface WidgetEvents {
    COMMIT_STEP: CommitAction['payload'];
    RESET_HISTORY: ResetHistoryAction['payload'];
    SKIP_STEP: SkipAction['payload'];
    GO_TO_STEP: GoToStepAction['payload'];
    BACK: void;
    NEXT: void;
    RESET: void;
    RESET_FORM: void;
  }
}

const middlewares: Middleware<WidgetState, ReducerActions>[] = [];

if (process.env.NODE_ENV === 'development') {
  middlewares.push(logger);
}

/**
 * Hook that provides access to all of the widget's functionalities. It provides methods to
 * navigate between widget steps, commit step data, skip steps, and submit the Widget data.
 */
export const useWidget = ({
  steps,
  onChange,
  onCommit,
  onSubmitLead,
  onUpdateLead,
  onClose,
  on: dispatchEvent = noop,
  initialState,
  enableReset,
  cancelIcon,
  widgetId,
  leadData,
  analyticsMap,
  onRequestTimeSlots,
}: UseWidgetProps): WidgetStateMachine => {
  const [state, dispatch] = useEnhancedReducer<
    ReducerActions,
    WidgetState,
    ThunkDispatch<ReducerActions, WidgetState>
  >(reducer, () => initState({ ...initialState, steps }), [
    thunkMiddleware,
    ...middlewares,
    useEventMiddleware<ReducerActions, WidgetState>(dispatchEvent),
  ]);
  const historyRef = useMutRef(state.history);
  const initialHistoryLength = initialState?.history?.length || 0;
  const stepIndex =
    initialHistoryLength === 0
      ? state.history.findIndex((historyStep) => historyStep.id === state.activeId)
      : state.history.findIndex((historyStep) => historyStep.id === state.activeId) -
        initialHistoryLength +
        1;
  const prevStepId = state.history[stepIndex]?.id;
  const transactionId = useRef(nanoid());

  const expectRenderWhen = (query: Record<string, unknown>, formData: Record<string, unknown>) => {
    const findedStep = steps.find((step) => {
      return Object.entries(query).every(
        ([key, value]) =>
          step[key as keyof StepProps] === value || step?.children?.props[key] === value,
      );
    });
    return !!findedStep?.renderWhen?.(formData);
  };

  const trackWidgetClosed = useCallback((): void => {
    pushGAEvent('widget-closed', {
      widgetId,
    });
    pushGAEvent('step-closed', {
      widgetId,
      stepId: state.activeId,
      prevStepId,
      stepIndex,
      transactionId: transactionId.current,
    });
  }, [prevStepId, state.activeId, stepIndex, widgetId]);

  useEffect(() => {
    const onBeforeUnload = (ev: BeforeUnloadEvent): void => {
      ev.preventDefault();
      trackWidgetClosed();
    };
    window.onbeforeunload = onBeforeUnload;
    return () => {
      window.onbeforeunload = null;
    };
  }, [trackWidgetClosed]);

  /**
   * Gets all data submitted from widget steps
   */
  const getFormData = useCallback(
    (): Record<string, unknown> => accumulateFormData(historyRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [historyRef.current],
  );

  useEffect(() => {
    onChange?.(getFormData());
  }, [onChange, getFormData, state.history]);

  /**
   * Retrieve step props by id
   */
  const getStepProps = (id: string): StepProps | undefined => state.stepPropsMap[id];

  const saveStep = (id: string, stepData: Record<string, unknown>): void => {
    const { analyticsMap: analyticsMapStep, name } = getStepProps(id) || {};
    const formData = getFormData();
    pushGAEvent('step-submitted', {
      ...getAnalyticsFields({ analyticsMap, source: formData }),
      ...getAnalyticsFields({ analyticsMap: analyticsMapStep, source: stepData }),
      stepId: state.activeId,
      prevStepId,
      stepIndex,
      widgetId,
      transactionId: transactionId.current,
      // contact data and address should not be send to GA, it's a private data
      ...(name && typeof stepData[name] !== 'object' ? { selected_value: stepData[name] } : {}),
    });
    dispatch({ type: 'COMMIT_STEP', payload: { id, stepData } });
  };

  /**
   * Submits step data to the widget state and navigate to next step
   */
  const commit = (
    id: string,
    stepData: Record<string, unknown>,
    initialNextStepId?: string,
  ): void => {
    dispatch((dispatch, getState) => {
      saveStep(id, stepData);
      const state = getState();
      const nextStepId = initialNextStepId ?? calculateNextStep(state.history, id, steps);

      onCommit(stepData);

      if (nextStepId !== id) {
        dispatch({
          type: 'RESET_HISTORY',
          payload: { id, nextStepId },
        });
      }
    });
  };

  const skip = (id: string, initialNextStepId?: string): void => {
    pushGAEvent('step-skipped', {
      stepId: state.activeId,
      prevStepId,
      stepIndex,
      widgetId,
      transactionId: transactionId.current,
    });
    dispatch((dispatch, getState) => {
      dispatch({ type: 'SKIP_STEP', payload: { id } });
      const state = getState();
      const nextStepId = initialNextStepId || calculateNextStep(state.history, id, steps);

      if (nextStepId !== id) {
        dispatch({
          type: 'RESET_HISTORY',
          payload: { id, nextStepId },
        });
      }
    });
  };

  /**
   * Retrieve step data by id
   */
  const getStep = <FD extends Record<string, unknown>>(id: string): StepState<FD> | undefined =>
    state.history.find((step) => step.id === id) as StepState<FD>;

  async function submit(target: WidgetSubmitTarget): Promise<void> {
    const formData = getFormData();
    if (target === 'update-lead') {
      try {
        await onUpdateLead?.(formData);
      } catch {
        /* empty */
      }
      return;
    }

    // check DAB upfront
    const isDirectAppointmentBookingUpfront = Boolean(formData.booking);
    // check DAB based on renderWhen
    const isDirectAppointmentBookingFromRW = expectRenderWhen(
      { isFirstBrokerBookingStep: true },
      formData,
    );
    formData.isDirectAppointmentBooking =
      isDirectAppointmentBookingUpfront || isDirectAppointmentBookingFromRW;

    try {
      await onSubmitLead?.({
        ...getSkippedFields(state.stepDataMap),
        ...formData,
        transactionId: transactionId.current,
      });
      pushGAEvent('widget-submitted', {
        widgetId,
      });
    } catch {
      pushGAEvent('widget-submitFailed', {
        widgetId,
      });
    }
  }

  const close = async (): Promise<void> => {
    onClose?.();
    trackWidgetClosed();
  };

  const reset = (): void => {
    pushGAEvent('widget-reset', {
      widgetId,
    });
    pushGAEvent('step-reset', {
      widgetId,
      stepId: state.activeId,
      prevStepId,
      stepIndex,
      transactionId: transactionId.current,
    });
    dispatch({ type: 'RESET' });
  };

  // Navigation
  const back = (): void => {
    dispatch((dispatch, getState) => {
      const { history, activeId } = getState();

      if (history.length && activeId == history[0]?.id) {
        close();
      }

      if (history.length < 1) {
        close();
      } else {
        pushGAEvent('step-back', {
          stepId: state.activeId,
          prevStepId,
          stepIndex,
          widgetId,
          transactionId: transactionId.current,
        });
        dispatch({ type: 'BACK' });
      }
    });
  };
  const next = (): void => {
    dispatch({ type: 'NEXT' });
  };
  const goTo = (id: string): void => {
    dispatch({ type: 'GO_TO_STEP', payload: { id } });
  };

  return {
    getFormData,
    commit,
    submit,
    close,
    skip,
    reset,
    getStep,
    getStepProps,
    prevStepId,
    onRequestTimeSlots,
    history: state.history,
    activeId: state.activeId,
    stepIndex,
    next,
    back,
    goTo,
    enableReset,
    cancelIcon,
    leadData,
    transactionId: transactionId.current,
    widgetId,
    expectRenderWhen,
  };
};
